import { DeleteForeverOutlined } from "@mui/icons-material";
import { Box, Icon, Stack, Typography, useTheme } from "@mui/material";
import i18next from "i18next";
import { uniqueId } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";

const thumbsContainer = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  marginTop: 16,
  width: "fit-content",
};

const thumb = {
  display: "inline-flex",
  borderRadius: 2,
  border: "1px solid #eaeaea",
  marginBottom: 8,
  marginRight: 8,
  width: 100,
  height: 100,
  padding: 4,
  boxSizing: "border-box",
  objectFit: "contain",
};

const thumbInner = {
  display: "flex",
  minWidth: 0,
  flex: "1 1 100%",
  overflow: "hidden",
};

const UploadInput = ({
  files = [],
  setFiles,
  multiple = true,
  accept = {
    "image/jpg": [],
    "image/jpeg": [],
    "image/png": [],
    "image/gif": [],
    "image/webp": [],
  },
  previewThumbs = true,
  secondary,
  callbackFunc,
  maxSize = 2,
  maxFiles = 5,
  primaryLabel = i18next.t("dragNDrop"),
  hasError = false,
  parentSx = {},
}) => {
  UploadInput.propTypes = {
    files: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    setFiles: PropTypes.func,
    multiple: PropTypes.bool,
    accept: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    previewThumbs: PropTypes.bool,
    secondary: PropTypes.any,
    callbackFunc: PropTypes.func,
    maxSize: PropTypes.number,
    maxFiles: PropTypes.number,
    primaryLabel: PropTypes.any,
    hasError: PropTypes.bool,
  };
  const theme = useTheme();
  const [filePreviewUrls, setFilePreviewUrls] = useState({});

  // Convert file to data URL for CSP-safe preview
  const createFilePreview = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
  };

  // Generate preview URLs when files change
  useEffect(() => {
    const generatePreviews = async () => {
      if (!files) return;
      
      const fileArray = Array.isArray(files) ? files : [files];
      const newPreviewUrls = {};
      
      await Promise.all(
        fileArray.map(async (file) => {
          if (file.file || file instanceof File) {
            // Handle local File objects
            const fileToRead = file.file || file;
            const dataUrl = await createFilePreview(fileToRead);
            newPreviewUrls[file.id] = dataUrl;
            // Also update the file object's preview property for backward compatibility
            file.preview = dataUrl;
          } else if (file.url || (typeof file === 'string' && file.startsWith('http'))) {
            // Handle storage URLs - use the URL directly as preview
            const imageUrl = file.url || file;
            newPreviewUrls[file.id] = imageUrl;
            file.preview = imageUrl;
          } else if (file.downloadURL) {
            // Handle Firebase storage objects with downloadURL
            newPreviewUrls[file.id] = file.downloadURL;
            file.preview = file.downloadURL;
          }
        })
      );
      
      setFilePreviewUrls(newPreviewUrls);
    };
    
    generatePreviews();
  }, [files]);

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    multiple: true,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (files && files.length >= maxFiles) {
        return;
      }
      const validFiles = acceptedFiles.filter(
        (file) => file.size <= maxSize * 1024 * 1024
      );

      if (validFiles.length === 0) {
        console.error("All files exceed the maximum size limit.");
        return;
      }

      if (files && files.length > 0) {
        const newFiles = validFiles.filter(
          (file) => !files.some((f) => f.path === file.path)
        );
        if (newFiles.length === 0) {
          console.error("All files are already in the list.");
          return;
        }
      }

      if (multiple) {
        setFiles((prevFiles) => [
          ...prevFiles,
          ...validFiles.map((file) =>
            Object.assign(file, {
              preview: null, // We'll use filePreviewUrls instead
              id: uniqueId("file-to-upload"),
              file: file,
            })
          ),
        ]);
      } else {
        setFiles(
          Object.assign(validFiles[0], {
            preview: null, // We'll use filePreviewUrls instead
            id: uniqueId("file-to-upload"),
            file: validFiles[0],
          })
        );
      }

      if (callbackFunc) {
        callbackFunc(files);
      }
    },
  });

  // Remove the old useEffect that revoked blob URLs since we're not using them anymore
  // useEffect(() => {
  //   if (multiple && files) {
  //     files.forEach((file) => URL.revokeObjectURL(file.preview));
  //   }
  // }, [files, multiple]);

  const thumbs = useMemo(
    () =>
      files &&
      (Array.isArray(files) ? files : [files]).map((file) => {
        const previewUrl = filePreviewUrls[file.id];
        return (
          <div style={thumb} key={`${uniqueId(file.id)}`}>
            <div
              style={{
                ...thumbInner,
                backgroundImage: previewUrl ? `url(${previewUrl})` : 'none',
                backgroundSize: "100%",
                backgroundRepeat: "no-repeat",
                objectFit: "cover",
                backgroundColor: previewUrl ? 'transparent' : '#f0f0f0',
              }}
              key={`${file.id}-internal-${uniqueId()}`}
            >
              <Icon
                onClick={(e) => {
                  e.stopPropagation();
                  setFiles((prevFiles) =>
                    Array.isArray(files) ? prevFiles.filter((f) => f.id !== file.id) : null
                  );
                }}
                sx={{
                  display: "inline-flex",
                  bgcolor: theme.palette.action.disabledBackground,
                  cursor: "pointer",
                  borderRadius: "4px",
                  zIndex: 2,
                  "&:hover": {
                    transform: "scale(1.1)",
                  },
                }}
              >
                <DeleteForeverOutlined color="error" size="small" />
              </Icon>
            </div>
          </div>
        );
      }),
    [files, setFiles, theme.palette.action.disabledBackground, filePreviewUrls]
  );

  return (
    <Box
      sx={parentSx}>
      <div
        {...getRootProps({ className: "dropzone" })}
        style={{
          width: "100%",
          height: "150px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: `1px dashed ${hasError ? theme.palette.error.main : theme.palette.primary.main
            }`,
          boxShadow: theme.shadows[8],
          cursor: "pointer",
        }}
      >
        <input {...getInputProps()} />
        <Stack>
          {primaryLabel && typeof primaryLabel === "string" ? (
            <Typography variant="caption" key={`one-${uniqueId()}`}>
              {primaryLabel}
            </Typography>
          ) : (
            primaryLabel
          )}
          {secondary && typeof secondary === "string" ? (
            <Typography
              variant="caption"
              component={"p"}
              key={`two-${uniqueId()}`}
            >
              {secondary}
            </Typography>
          ) : (
            secondary
          )}
        </Stack>
      </div>

      {previewThumbs && <aside style={thumbsContainer}>{thumbs}</aside>}
    </Box>
  );
};

export default UploadInput;
